import { useState, useCallback, useMemo, useEffect } from "react";

export const useMultiSelect = (options, onChange, defaultSelected) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [tempSelectedOptions, setTempSelectedOptions] = useState([]);

    // Update selected options when defaultSelected changes
    useEffect(() => {
        if (defaultSelected && defaultSelected.length > 0) {
            // Ensure we're using the full option objects from the options array
            const validSelectedOptions = options?.filter((option) =>
                defaultSelected.some(
                    (defaultOpt) => defaultOpt.id === option.id
                )
            );
            setSelectedOptions(validSelectedOptions);
            setTempSelectedOptions(validSelectedOptions);
        }
    }, [defaultSelected, options]);

    const filteredOptions = useMemo(() => {
        return options?.filter((option) =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [options, searchTerm]);

    const handleToggle = useCallback(() => {
        setIsOpen((prev) => !prev);
        if (!isOpen) {
            // Reset temp selections to current selections when opening
            setTempSelectedOptions(selectedOptions);
        }
    }, [isOpen, selectedOptions]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setSearchTerm("");
        setTempSelectedOptions(selectedOptions);
    }, [selectedOptions]);

    const handleSearch = useCallback((value) => {
        setSearchTerm(value);
    }, []);

    const handleOptionToggle = useCallback((option) => {
        setTempSelectedOptions((prev) => {
            const isSelected = prev.some((item) => item.id === option.id);
            if (isSelected) {
                return prev.filter((item) => item.id !== option.id);
            }
            return [...prev, option];
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setTempSelectedOptions((prev) =>
            prev.length === filteredOptions.length ? [] : [...filteredOptions]
        );
    }, [filteredOptions]);

    const handleCancel = useCallback(() => {
        // Reset to the last confirmed selection
        setTempSelectedOptions(selectedOptions);
        setIsOpen(false);
        setSearchTerm("");
    }, [selectedOptions]);

    const handleApply = useCallback(() => {
        // Update both local state and notify parent
        setSelectedOptions(tempSelectedOptions);
        onChange(tempSelectedOptions);
        setIsOpen(false);
        setSearchTerm("");
    }, [tempSelectedOptions, onChange]);

    return {
        isOpen,
        searchTerm,
        selectedOptions: tempSelectedOptions,
        filteredOptions,
        handleToggle,
        handleSearch,
        handleOptionToggle,
        handleSelectAll,
        handleCancel,
        handleApply,
        handleClose,
    };
};
